import { GraphicsTriangle } from '@/components/Graphics/Triangle';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { HTMLAttributes } from 'vue';
import type { HeaderClickedEventProps } from '~/composables/events/headerEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import type { HeaderClicked, MenuHeaderClicked } from '~/types/mixpanel';
import IconMaterialSymbolsLightAccountCircle from '~icons/material-symbols-light/account-circle-outline';
import IconMaterialSymbolsLightRealStateAgent from '~icons/material-symbols-light/real-estate-agent-outline-sharp';
import IconPilarOffMarket from '~icons/pilar/off-market';
import IconPilarPropertyOwner from '~icons/pilar/property-owner';
export { default as PortalHeader } from './PortalHeader.vue';

export type HeaderNavButtonLabel =
	| 'Logo'
	| 'Comprar'
	| 'Vender'
	| 'Sobre a Pilar'
	| 'Entrar';

export type HeaderSubmenuButtonsLabel =
	| 'Off-Market'
	| 'Exclusivos PilarHomes'
	| 'Sou proprietário'
	| 'Sou corretor';

export const HeaderNavButtonEventsMapper: Record<
	HeaderNavButtonLabel,
	keyof HeaderClicked
> = {
	Logo: MixpanelProps.CLICK_LOGO,
	Comprar: MixpanelProps.CLICK_BUY,
	Vender: MixpanelProps.CLICK_SELL,
	'Sobre a Pilar': MixpanelProps.CLICK_ABOUT_PILAR,
	Entrar: MixpanelProps.CLICK_LOGIN
};

export const HeaderSubmenuEventsMapper: Record<
	HeaderSubmenuButtonsLabel,
	keyof MenuHeaderClicked
> = {
	'Off-Market': MixpanelProps.CLICK_OFF_MARKET,
	'Exclusivos PilarHomes': MixpanelProps.CLICK_EXCLUSIVE,
	'Sou proprietário': MixpanelProps.CLICK_OWNER,
	'Sou corretor': MixpanelProps.CLICK_FORMER
};

interface Link {
	label: HeaderNavButtonLabel;
	route: string;
}

export interface HeaderProps {
	isVisible?: boolean;
	variant?: BackgroundVariantProps['variant'];
	transform?: NavVariantProps['transform'];
	links?: Link[];
	class?: HTMLAttributes['class'];
	fixed?: boolean;
}

export const navVariants = cva(
	'z-40 font-matter-sq text-lg transition-[transform,color,background-color] duration-300 ease-in-out',
	{
		variants: {
			variant: { primary: 'bg-primary', translucide: 'bg-transparent' },
			transform: { none: '', down: 'translate-y-0', up: '-translate-y-full' }
		},
		defaultVariants: { variant: 'primary', transform: 'none' }
	}
);

export const backgroundVariants = cva('font-matter-sq text-lg', {
	variants: {
		variant: { primary: 'bg-primary-light-4', translucide: 'bg-primary/20' }
	},
	defaultVariants: { variant: 'primary' }
});

export const navOptionVariants = cva(
	'relative flex items-center text-base leading-[26px]',
	{
		variants: {
			variant: { default: 'font-normal', description: 'font-medium' }
		},
		defaultVariants: { variant: 'default' }
	}
);

export const portalHeaderNavLoggedInfosName = cva('', {
	variants: { variant: { default: 'hidden lg:flex', mobile: 'flex' } },
	defaultVariants: { variant: 'default' }
});

export const portalHeaderNavLoggedInfosEmail = cva('', {
	variants: { variant: { default: 'hidden', mobile: 'flex' } },
	defaultVariants: { variant: 'default' }
});

export type BackgroundVariantProps = VariantProps<typeof backgroundVariants>;
export type NavVariantProps = VariantProps<typeof navVariants>;
export type NavOptionVariantProps = VariantProps<typeof navOptionVariants>;
export type PortalHeaderNavLoggedInfosNameVariantProps = VariantProps<
	typeof portalHeaderNavLoggedInfosName
>;
export type PortalHeaderNavLoggedInfosEmailVariantProps = VariantProps<
	typeof portalHeaderNavLoggedInfosEmail
>;

export type Region = { name: string; city: string; slug: string };
export type City = {
	name: string;
	slug: string;
	state: string;
	country: string;
	regions: Region[];
};
export const cities: City[] = [
	{
		name: 'São Paulo',
		slug: 'sao-paulo',
		state: 'SP',
		country: 'Brasil',
		regions: [
			{ name: 'Itaim Bibi', city: 'sao-paulo', slug: 'itaim-bibi' },
			{ name: 'Jardim Paulista', city: 'sao-paulo', slug: 'jardim-paulista' },
			{
				name: 'Vila Nova Conceição',
				city: 'sao-paulo',
				slug: 'vila-nova-conceicao'
			},
			{ name: 'Jardim América', city: 'sao-paulo', slug: 'jardim-america' },
			{ name: 'Higienópolis', city: 'sao-paulo', slug: 'higienopolis' }
		]
	},
	{
		name: 'Alphaville',
		slug: 'alphaville',
		state: 'SP',
		country: 'Brasil',
		regions: [
			{
				name: 'Alphaville Empresarial',
				city: 'barueri',
				slug: 'alphaville-empresarial'
			},
			{
				name: 'Condomínio Alphaville Residencial 0',
				city: 'barueri',
				slug: 'condominio-alphaville-residencial-0'
			},
			{
				name: 'Condomínio Residencial Tamboré',
				city: 'barueri',
				slug: 'condominio-residencial-tambore'
			},
			{
				name: 'Condomínio Alphaville Residencial 2',
				city: 'barueri',
				slug: 'condominio-alphaville-residencial-2'
			},
			{
				name: 'Alphaville Industrial',
				city: 'barueri',
				slug: 'alphaville-industrial'
			}
		]
	},
	{
		name: 'Interior',
		slug: 'interior',
		state: 'SP',
		country: 'Brasil',
		regions: [
			{
				name: 'Condomínio Fazenda Boa Vista',
				city: 'porto-feliz',
				slug: 'condominio-fazenda-boa-vista'
			},
			{
				name: 'Condomínio Boa Vista Village',
				city: 'porto-feliz',
				slug: 'condominio-boa-vista-village'
			},
			{
				name: 'Condomínio Fazenda Vila Real de Itu',
				city: 'itu',
				slug: 'condominio-fazenda-vila-real-de-itu'
			},
			{
				name: 'Condomínio Terras de São José',
				city: 'itu',
				slug: 'condominio-terras-de-sao-jose'
			},
			{
				name: 'Quinta da Baroneza',
				city: 'braganca-paulista',
				slug: 'quinta-da-baroneza'
			}
		]
	},
	{
		name: 'Litoral Norte',
		slug: 'litoral-norte',
		state: 'SP',
		country: 'Brasil',
		regions: [
			{
				name: 'Riviera de São Lourenço',
				city: 'bertioga',
				slug: 'riviera-de-sao-lourenco'
			},
			{ name: 'Baleia', city: 'sao-sebastiao', slug: 'baleia' },
			{ name: 'Juquehy', city: 'sao-sebastiao', slug: 'juquehy' },
			{
				name: 'Praia de Camburí',
				city: 'sao-sebastiao',
				slug: 'praia-de-camburi'
			},
			{
				name: 'Praia de Maresias',
				city: 'sao-sebastiao',
				slug: 'praia-de-maresias'
			}
		]
	},
	{
		name: 'Litoral Sul',
		slug: 'litoral-sul',
		state: 'SP',
		country: 'Brasil',
		regions: [
			{ name: 'Cidade Jardim Tom', city: 'guaruja', slug: 'cidade-jardim-tom' },
			{ name: 'Iporanga', city: 'guaruja', slug: 'iporanga' },
			{ name: 'Jardim Acapulco', city: 'guaruja', slug: 'jardim-acapulco' },
			{ name: 'Pitangueiras', city: 'guaruja', slug: 'pitangueiras' },
			{ name: 'Enseada', city: 'guaruja', slug: 'enseada' }
		]
	}
];

export type NavDefaultOptionBasicConfig = {
	label: string | Component;
	description?: string | Component;
	icon?: {
		component: Component;
		bindings: Record<string, unknown>;
	};
	route?: string;
	eventProps?: HeaderClickedEventProps;
	eventCallback?: () => void;
};

export type NavDefaultOptionConfig = NavDefaultOptionBasicConfig & {
	subOptions?: NavDefaultOptionBasicConfig[];
};

export const highlightBuyOptionsData = [
	{
		label: 'Exclusivos PilarHomes',
		description: 'Imóveis únicos, listados apenas no PilarHomes.',
		route: '/venda/imoveis/sao-paulo-sp-brasil?isExclusive=true',
		icon: {
			component: GraphicsTriangle,
			bindings: {
				class: 'stroke-[2px] p-[3px] text-transparent',
				stroke: 'beige',
				isStrokeApplied: true
			}
		}
	},
	{
		label: 'Off-Market',
		description:
			'Explore oportunidades selecionadas a dedo, disponíveis apenas aqui.',
		route: '/off-market',
		icon: {
			component: IconPilarOffMarket,
			bindings: { class: 'p-[2px] text-beige' }
		}
	}
];

export const highlightBuyOptions: NavDefaultOptionConfig[] =
	highlightBuyOptionsData.map(option => ({
		...option,
		eventProps: {
			clickedButton: option.label,
			destinationUrl: option.route,
			action: 'buy'
		}
	}));

const sellNavOptionsData = [
	{
		label: 'Sou proprietário',
		route: '/anunciar-imovel',
		icon: {
			component: IconPilarPropertyOwner,
			bindings: {
				class: 'pb-[3px] pl-[3px] text-beige size-5',
				isStrokeApplied: true
			}
		}
	},
	{
		label: 'Sou corretor',
		route: '/?typeform=lead-broker',
		icon: {
			component: IconMaterialSymbolsLightRealStateAgent,
			bindings: {
				class: 'pb-[3px] text-beige size-6',
				isStrokeApplied: true
			}
		}
	},
	{
		label: 'Off-Market',
		route: '/off-market',
		icon: {
			component: IconPilarOffMarket,
			bindings: {
				class: 'pb-[2px] size-5 text-beige'
			}
		}
	}
];

export const sellOptions: NavDefaultOptionConfig[] = sellNavOptionsData.map(
	option => ({
		...option,
		eventProps: {
			clickedButton: option.label,
			destinationUrl: option.route,
			action: 'sell'
		}
	})
);

export const profileOptions: NavDefaultOptionBasicConfig[] = [
	{
		label: 'Meus dados',
		icon: {
			component: IconMaterialSymbolsLightAccountCircle,
			bindings: { class: 'size-6 pl-[3px] text-beige' }
		},
		route: '/perfil'
	}
];
